import React, { useCallback, useEffect } from 'react'
import { TransferAmount } from '../TransferAmount/TransferAmount'
import numberInputFormatter from '../../utils/inputFormatters'
import { LabeledSelect } from '@apex/component-library'
import { useFormatNumber } from '../../utils/intlHelpers'
import { useFormikContext } from 'formik'
import { ACHTransferValues } from './types'
import { buildACHAccountOptions, buildAccountOption } from '../../utils/optionBuilders'
import { AchRelationshipResponsePayload, SearchAccountsResponsePayload } from '@Types'
import { TransferDirection } from '../../api/useAchSubmission'
export interface TransferDetailsProps {
  achRels?: AchRelationshipResponsePayload[]
  account?: SearchAccountsResponsePayload | null
  amountAvailable: number
  transferDirection?: TransferDirection
}

const TransferDetails = ({ achRels, account, amountAvailable }: TransferDetailsProps) => {
  const formik = useFormikContext<ACHTransferValues>()

  const numberFormatter = useFormatNumber()
  const clearTransferAmount = () => {
    formik.setFieldValue('transferAmountDisplay', '')
    formik.setFieldValue('transferAmountRaw', 0)
    formik.setFieldTouched('transferAmountRaw', false)
  }

  const clearTransferAmountMemo = useCallback(clearTransferAmount, [])

  useEffect(() => {
    if (formik.values.totalBalanceCheckbox) {
      clearTransferAmountMemo()
    }
  }, [formik.values.totalBalanceCheckbox, clearTransferAmountMemo])

  const acctOption = buildAccountOption(account, numberFormatter, amountAvailable)
  const options = buildACHAccountOptions(achRels, acctOption)

  return (
    <>
      <TransferAmount
        value={formik.values.transferAmountDisplay}
        isDisabled={formik.values.totalBalanceCheckbox}
        errorMessage={formik.errors.transferAmountDisplay || formik.errors.transferAmountRaw}
        handleBlur={formik.handleBlur}
        handleAmountChange={(e) => {
          formik.setValues({
            ...formik.values,
            transferAmountDisplay: numberInputFormatter(e.target.value, numberFormatter).display,
            transferAmountRaw: numberInputFormatter(e.target.value, numberFormatter).raw as number
          })
        }}
        isInvalid={
          (!!formik.errors.transferAmountRaw && formik.touched.transferAmountRaw) ||
          (!!formik.errors.transferAmountRaw && formik.values.totalBalanceCheckbox)
        }
        totalBalanceChecked={formik.values.totalBalanceCheckbox}
        handleCheckboxChange={formik.handleChange}
      />
      <LabeledSelect
        label='From Account'
        aria-label='From Account'
        name='fromAccount'
        isRequired
        value={formik.values.fromAccount}
        onChange={(e) => {
          const optionAccountName = options?.from.find((item) => item.value === e.target.value)?.text
          formik.setValues({
            ...formik.values,
            fromAccountName: optionAccountName,
            fromAccount: e.target.value,
            // if setting to account number and To Account is account number
            // or setting to ach relationship and To Account is ach relationship
            // then clear the To Account
            ...(((e.target.value === account?.accountNumber && formik.values.toAccount === account?.accountNumber) ||
              (e.target.value !== account?.accountNumber && formik.values.toAccount !== account?.accountNumber)) && {
              toAccount: '',
              toAccountName: ''
            }),
            // Clear Distribution fields
            distributionReason: '',
            federalTaxWithholdingType: undefined,
            federalTaxWithholdingAmountRaw: undefined,
            federalTaxWithholdingAmountDisplay: undefined
          })
          formik.setFieldTouched('distributionReason', false)
        }}
      >
        <option value=''></option>
        {options?.from.map((option) => (
          <option key={option.key} value={option.value}>
            {option.text}
          </option>
        ))}
      </LabeledSelect>
      <LabeledSelect
        label='To Account'
        aria-label='To Account'
        isRequired
        name='toAccount'
        value={formik.values.toAccount}
        onChange={(e) => {
          const optionAccountName = options?.from.find((item) => item.value === e.target.value)?.text
          formik.setValues({
            ...formik.values,
            toAccountName: optionAccountName,
            toAccount: e.target.value,
            // if setting to account number and From Account is account number
            // or setting to ach relationship and From Account is ach relationship
            // then clear the From Account
            ...(((e.target.value === account?.accountNumber && formik.values.fromAccount === account?.accountNumber) ||
              (e.target.value !== account?.accountNumber && formik.values.fromAccount !== account?.accountNumber)) && {
              fromAccount: '',
              fromAccountName: ''
            })
          })
        }}
      >
        <option value=''></option>
        {options?.to.map((option) => (
          <option key={option.key} value={option.value}>
            {option.text}
          </option>
        ))}
      </LabeledSelect>
    </>
  )
}

export default TransferDetails
